import { useState, useEffect } from 'react'
import Loader from "../loader/loader";
import ToastrService from "../../services/toastService";
import Pagination from "../Pagination/Pagination";
import { FiMail, FiInbox } from "react-icons/fi";
import { FaPhone } from "react-icons/fa6";
import { BsChatDots } from "react-icons/bs";
import CampaignFormDrawer from './CampaignFormDrawer';
import { useNavigate } from "react-router-dom";
import { useGetCampaignListMutation, useGetCampaignSummaryQuery, useGetCampaignByIdMutation, useUpdateCampaignStatusMutation } from './Api';
import Swal from "sweetalert2";

const CAMPAIGN_TYPES = [
    { label: 'All Types', value: 'all' },
    { label: 'Inbound', value: 'inbound' },
    { label: 'Outbound', value: 'outbound' },
    // { label: 'Both', value: 'both' },
];

const CAMPAIGN_STATUSES = [
    { label: 'All Status', value: 'all' },
    { label: 'Active', value: 'active' },
    { label: 'Inactive', value: 'inactive' },
];

const channelMap = {
    Voice: { icon: <FaPhone size={15} />, label: "Voice", badgeClass: "bg-soft-blue text-primary" },
    Chat: { icon: <BsChatDots size={15} />, label: "Chat", badgeClass: "bg-indigo text-indigo" },
    Email: { icon: <FiMail size={15} />, label: "Email", badgeClass: "bg-warm-orange text-burnt-orange" },
};


const CampaignList = () => {

    const [isDrawerOpen, setIsDrawerOpen] = useState(false);
    const [drawerMode, setDrawerMode] = useState("add");
    const [currentPage, setCurrentPage] = useState(1);
    const [rowsPerPage, setRowsPerPage] = useState(5);
    const [totalRecords, setTotalRecords] = useState(0);
    const [totalPages, setTotalPages] = useState(1);
    const [search, setSearch] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState(search);

    const [campaigns, setCampaigns] = useState([]);
    const [selectedCampaign, setSelectedCampaign] = useState(null);
    const [campaignType, setCampaignType] = useState(CAMPAIGN_TYPES[0].value);
    const [campaignStatus, setCampaignStatus] = useState(CAMPAIGN_STATUSES[0].value);

    const [isFirstLoad, setIsFirstLoad] = useState(true);

    const [getCampaigns, { isLoading: isCampaignLoading }] = useGetCampaignListMutation();

    const { data: campaignSummary, refetch: refetchCampaignSummary, } = useGetCampaignSummaryQuery();
    const summary = campaignSummary?.data?.summary;

    const [updateCampaignStatus, { isLoading: isStatusUpdating }] = useUpdateCampaignStatusMutation();
    const [refreshCampaigns, setRefreshCampaigns] = useState(0);
    const [updatingCampaignId, setUpdatingCampaignId] = useState(null);

    const [getCampaignById] = useGetCampaignByIdMutation();


    const startEntry = totalRecords === 0 ? 0 : (currentPage - 1) * rowsPerPage + 1;

    const endEntry = Math.min(currentPage * rowsPerPage, totalRecords);

    const navigate = useNavigate();

    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedSearch(search);
        }, 500);

        return () => clearTimeout(timer);
    }, [search]);

    useEffect(() => {
        const fetchCampaigns = async () => {
            try {
                const payload = {
                    page: currentPage,
                    limit: rowsPerPage,
                    search: debouncedSearch.trim(),
                    campaignType,
                    campaignStatus,
                };

                const response = await getCampaigns(payload).unwrap();

                setCampaigns(response?.data?.campaigns || []);
                setTotalRecords(response?.data?.pagination?.total || 0);
                setTotalPages(response?.data?.pagination?.totalPages || 1);

            } catch (error) {
                console.error("Error fetching campaigns:", error);
            } finally {
                setIsFirstLoad(false)
            }
        };

        fetchCampaigns();
    }, [currentPage, rowsPerPage, debouncedSearch, campaignType, campaignStatus, refreshCampaigns]);


    const handleCreate = () => {
        setDrawerMode("add");
        setSelectedCampaign(null);
        setIsDrawerOpen(true);
    };

    const handleCampaignSaved = () => {
        setIsDrawerOpen(false);
        setRefreshCampaigns((prev) => prev + 1);
        refetchCampaignSummary();
    };

    const handleEdit = async (campaign) => {
        try {
            const payload = {
                id: campaign.id
            };
            const res = await getCampaignById(payload).unwrap();
            setDrawerMode("edit");
            setSelectedCampaign(res?.data);
            setIsDrawerOpen(true);
        } catch (error) {
            console.error("Error fetching campaign:", error);
        }

    };

    const handleAssign = (campaign) => {
        navigate("/campaign/agent-assignment", {
            state: {
                campaignId: campaign.id,
            },
        });
    };

    const handleStatusChange = async (campaign) => {
        const isActive = campaign.campaign_status?.toLowerCase() === "active";
        const newStatus = isActive ? "inactive" : "active";

        try {
            setUpdatingCampaignId(campaign.id);
            const payload = {
                id: campaign.id,
                status: newStatus
            };
            const res = await updateCampaignStatus(payload).unwrap();
            if (res?.data?.updated === true) {
                ToastrService.success(res?.message || `Campaign ${newStatus === "active" ? "activated" : "deactivated"} successfully!`);
                setRefreshCampaigns((prev) => prev + 1);
                refetchCampaignSummary();
            } else {
                Swal.fire({
                    icon: "info",
                    title: "Cannot Activate",
                    text: res?.message || "Campaign status cannot be updated.",
                    confirmButtonText: "OK"
                });
                // ToastrService.error(res?.message || "Failed to update campaign status");
            }
        } catch (error) {
            console.error("Error updating campaign status:", error);
        } finally {
            setUpdatingCampaignId(null);
        }
    };


    return (
        <div>
            {isFirstLoad ? <Loader /> :
                <>
                    <div>
                        {/* Header */}
                        < div className="page-header">
                            <div className="page-header-left d-flex align-items-center">
                                <ul className="breadcrumb mb-0">
                                    <li className="breadcrumb-item">Campaigns</li>
                                    <li className="breadcrumb-item active">Campaign List</li>
                                </ul>
                            </div>
                        </div>

                        <div className="main-content">
                            <div className="row align-items-center mb-3">
                                <div className="col-lg-8 col-md-7">
                                    <h3 className="h4 mb-1 drawer-header">
                                        Campaign List
                                    </h3>

                                    <p className="mb-0 text-muted">
                                        Manage all inbound and outbound campaigns across the contact centre
                                    </p>
                                </div>

                                <div className="col-lg-4 col-md-5 mt-3 mt-md-0">
                                    <div className="d-flex justify-content-md-end flex-wrap gap-2">
                                        <button className="btn btn-primary px-3 py-2" onClick={handleCreate}>
                                            + Create Campaign
                                        </button>
                                    </div>
                                </div>

                                <div className="row g-3 mb-3">
                                    <div className="col-xl-3 col-md-6">
                                        <div className="card shadow-sm rounded-4">
                                            <div className="card-body">
                                                <h6 className="text-uppercase text-secondary fw-semibold small mb-2">
                                                    Total Campaigns
                                                </h6>

                                                <h1 className="fw-bold mb-2"> {summary?.total_campaigns ?? 0}</h1>

                                                <span className="badge rounded-pill bg-soft-success text-success">
                                                    {summary?.active_campaigns ?? 0} Active
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="col-xl-3 col-md-6">
                                        <div className="card shadow-sm rounded-4">
                                            <div className="card-body">
                                                <h6 className="text-uppercase text-secondary fw-semibold small mb-2">
                                                    Agents Assigned
                                                </h6>

                                                <h1 className="fw-bold mb-2">{summary?.total_agents ?? 0}</h1>

                                                <span className="badge rounded-pill bg-info-blue text-info-blue">
                                                    Across all campaigns
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="col-xl-3 col-md-6">
                                        <div className="card shadow-sm rounded-4">
                                            <div className="card-body">
                                                <h6 className="text-uppercase text-secondary fw-semibold small mb-2">
                                                    Inbound
                                                </h6>

                                                <h1 className="fw-bold mb-2">{summary?.inbound_campaigns ?? 0}</h1>

                                                <span className="badge rounded-pill bg-soft-orange text-orange">
                                                    Voice + Chat
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="col-xl-3 col-md-6">
                                        <div className="card shadow-sm rounded-4">
                                            <div className="card-body">
                                                <h6 className="text-uppercase text-secondary fw-semibold small mb-2">
                                                    Outbound
                                                </h6>

                                                <h1 className="fw-bold mb-2">{summary?.outbound_campaigns ?? 0}</h1>

                                                <span className="badge rounded-pill bg-soft-success text-success">
                                                    Sales + Support
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                </div>


                                <div className="card p-3 mb-4 rounded-4">
                                    <div className="card shadow-sm border-0 p-3 mb-4">
                                        <div className="d-flex flex-column flex-lg-row justify-content-between align-items-start align-items-lg-center mb-4 gap-3">

                                            {/* Left */}
                                            <div>
                                                <h5 className="mb-1 fw-semibold">
                                                    All Campaigns
                                                </h5>

                                                <small className="text-muted">
                                                    {totalRecords || 0} campaigns configured
                                                </small>
                                            </div>

                                            {/* Right */}
                                            <div className="d-flex flex-column flex-sm-row flex-wrap gap-2 ms-lg-auto">

                                                {/* Search */}
                                                <input
                                                    type="text"
                                                    className="form-control form-control-sm campaign-search"
                                                    placeholder="Search campaigns..."
                                                    value={search}
                                                    onChange={(e) => {
                                                        setSearch(e.target.value);
                                                        setCurrentPage(1);
                                                    }}
                                                />

                                                <select className="form-select form-select-sm campaign-filter"
                                                    value={campaignType}
                                                    onChange={(e) => {
                                                        setCampaignType(e.target.value);
                                                        setCurrentPage(1);
                                                    }}
                                                >
                                                    {CAMPAIGN_TYPES.map((type) => (
                                                        <option key={type.value} value={type.value}>
                                                            {type.label}
                                                        </option>
                                                    ))}
                                                </select>

                                                <select className="form-select form-select-sm campaign-filter"
                                                    value={campaignStatus}
                                                    onChange={(e) => {
                                                        setCampaignStatus(e.target.value);
                                                        setCurrentPage(1);
                                                    }}
                                                >
                                                    {CAMPAIGN_STATUSES.map((type) => (
                                                        <option key={type.value} value={type.value}>
                                                            {type.label}
                                                        </option>
                                                    ))}
                                                </select>
                                            </div>
                                        </div>

                                    </div>

                                    <div className="table-responsive">
                                        <table className="table table-hover align-middle mb-0">
                                            <thead>
                                                <tr>
                                                    <th>Campaign Name</th>
                                                    <th>Type</th>
                                                    <th>Channel</th>
                                                    <th>Agents</th>
                                                    <th>Manager</th>
                                                    <th>Status</th>
                                                    <th>Created</th>
                                                    <th>Actions</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {isCampaignLoading ? (
                                                    <tr>
                                                        <td colSpan="8" className="text-center py-5">
                                                            <div className="d-flex justify-content-center align-items-center gap-2">
                                                                <div
                                                                    className="spinner-border spinner-border-sm text-primary"
                                                                    role="status"
                                                                />

                                                                <span className="text-muted">
                                                                    Loading campaigns...
                                                                </span>
                                                            </div>
                                                        </td>
                                                    </tr>
                                                ) : campaigns.length > 0 ? (
                                                    campaigns.map((campaign) => (
                                                        <tr key={campaign.id}>
                                                            <td>
                                                                <div className="fw-semibold">
                                                                    {campaign.campaign_name || "-"}
                                                                </div>

                                                                <small className="text-muted d-inline-block text-truncate w-75"
                                                                    title={campaign.description || ""}>
                                                                    {campaign.description || "-"}
                                                                </small>
                                                            </td>

                                                            <td>
                                                                {campaign.campaign_type ? (
                                                                    <span
                                                                        className={`badge rounded-pill ${campaign.campaign_type === "Inbound"
                                                                            ? "bg-soft-success text-success"
                                                                            : "bg-soft-blue text-primary"
                                                                            }`}
                                                                        style={{ width: "70px" }}
                                                                    >
                                                                        {campaign.campaign_type}
                                                                    </span>
                                                                ) : (
                                                                    <span className="text-muted">N/A</span>
                                                                )}
                                                            </td>

                                                            <td>

                                                                {campaign.channel ? (
                                                                    <div className="d-flex align-items-center flex-wrap gap-1">
                                                                        {campaign.channel.split(" + ").map((channel) => {
                                                                            const channelData = channelMap[channel.trim()];

                                                                            return (
                                                                                <span
                                                                                    key={channel}
                                                                                    className={`badge rounded-pill d-inline-flex align-items-center gap-1 ${channelData?.badgeClass || "bg-gray-100 text-secondary"
                                                                                        }`}
                                                                                >
                                                                                    {channelData?.icon}
                                                                                    {channel.trim()}
                                                                                </span>
                                                                            );
                                                                        })}
                                                                    </div>
                                                                ) : (
                                                                    <span className="text-muted">N/A</span>
                                                                )}
                                                                {/* {campaign.channel ? (
                                                                    <span className="d-inline-flex align-items-center flex-wrap gap-1">
                                                                        {campaign.channel.split(" + ").map((channel, index, arr) => (
                                                                            <React.Fragment key={channel}>
                                                                                <span className="d-inline-flex align-items-center gap-1">
                                                                                    {channelMap[channel.trim()]?.icon}
                                                                                    <span>{channel.trim()}</span>
                                                                                </span>

                                                                                {index < arr.length - 1 && <span>+</span>}
                                                                            </React.Fragment>
                                                                        ))}
                                                                    </span>
                                                                ) : (
                                                                    <span className="text-muted">N/A</span>
                                                                )} */}
                                                            </td>

                                                            <td>
                                                                {campaign.queue_members_count !== null &&
                                                                    campaign.queue_members_count !== undefined ? (
                                                                    <span className="badge rounded-pill bg-soft-primary text-primary" style={{ width: "70px" }}>
                                                                        {campaign.queue_members_count} agents
                                                                    </span>
                                                                ) : (
                                                                    <span className="text-muted">N/A</span>
                                                                )}
                                                            </td>

                                                            <td>{campaign.manager_name || "N/A"}</td>

                                                            <td>
                                                                {campaign.campaign_status ? (
                                                                    <span
                                                                        className={`badge rounded-pill ${campaign.campaign_status === "Active"
                                                                            ? "bg-soft-success text-success"
                                                                            : "bg-gray-100 text-secondary"
                                                                            }`} style={{ width: "70px" }}
                                                                    >
                                                                        ● {campaign.campaign_status}
                                                                    </span>
                                                                ) : (
                                                                    <span className="text-muted">N/A</span>
                                                                )}
                                                            </td>

                                                            <td>{campaign.created_at || "N/A"}</td>

                                                            <td>
                                                                <div className="d-flex align-items-center gap-2">

                                                                    {/* Edit - Always Visible */}
                                                                    <button
                                                                        className="btn btn-sm bg-soft-blue text-primary border-0 rounded-2 px-3 py-1"
                                                                        onClick={() => handleEdit(campaign)}
                                                                    >
                                                                        Edit
                                                                    </button>

                                                                    {campaign.campaign_status?.toLowerCase() === "active" ? (
                                                                        <>
                                                                            <button
                                                                                className="btn btn-sm bg-muted-slate text-dark border-0 rounded-2 px-3 py-1"
                                                                                onClick={() => handleAssign(campaign)}
                                                                            >
                                                                                Assign
                                                                            </button>

                                                                            <button
                                                                                className="btn btn-sm bg-soft-danger text-danger border-0 rounded-2 px-3 py-1"
                                                                                onClick={() => handleStatusChange(campaign)}
                                                                                disabled={updatingCampaignId === campaign.id}
                                                                            >
                                                                                {updatingCampaignId === campaign.id ? (
                                                                                    <>
                                                                                        <span
                                                                                            className="spinner-border spinner-border-sm me-1"
                                                                                            role="status"
                                                                                            aria-hidden="true"
                                                                                        />
                                                                                        Updating...
                                                                                    </>
                                                                                ) : (
                                                                                    "Deactivate"
                                                                                )}
                                                                            </button>
                                                                        </>
                                                                    ) : (
                                                                        <button
                                                                            className="btn btn-sm bg-soft-success text-success border-0 rounded-2 px-3 py-1"
                                                                            onClick={() => handleStatusChange(campaign)}
                                                                            disabled={updatingCampaignId === campaign.id}
                                                                        >
                                                                            {updatingCampaignId === campaign.id ? (
                                                                                <>
                                                                                    <span
                                                                                        className="spinner-border spinner-border-sm me-1"
                                                                                        role="status"
                                                                                        aria-hidden="true"
                                                                                    />
                                                                                    Updating...
                                                                                </>
                                                                            ) : (
                                                                                "Activate"
                                                                            )}
                                                                        </button>
                                                                    )}

                                                                </div>
                                                            </td>

                                                        </tr>
                                                    ))
                                                ) : (
                                                    <tr>
                                                        <td colSpan="9" className="text-center py-5">
                                                            <div className="text-muted">
                                                                <FiInbox className="fs-1 mx-auto d-block mb-2" />

                                                                <h6 className="fw-semibold mb-1">
                                                                    No campaigns found
                                                                </h6>

                                                                <small>
                                                                    {search ||
                                                                        campaignType !== "all" ||
                                                                        campaignStatus !== "all"
                                                                        ? "No campaigns match your current filters."
                                                                        : "There are no campaigns configured yet."}
                                                                </small>
                                                            </div>
                                                        </td>
                                                    </tr>
                                                )}

                                            </tbody>
                                        </table>
                                        {/* Pagination */}

                                        <div className="d-flex flex-column flex-md-row justify-content-between align-items-center mt-3 gap-2">

                                            <div className="d-flex align-items-center gap-1 small">

                                                <span>Show</span>

                                                <select
                                                    className="form-select form-select-sm page-size-select"
                                                    value={rowsPerPage}
                                                    onChange={(e) => {
                                                        setRowsPerPage(Number(e.target.value));
                                                        setCurrentPage(1);
                                                    }}
                                                >
                                                    <option value={5}>5</option>
                                                    <option value={10}>10</option>
                                                    <option value={15}>15</option>
                                                </select>

                                                <span>entries</span>

                                            </div>

                                            <div className="small text-muted">
                                                Showing {startEntry} to {endEntry} of {totalRecords} entries
                                            </div>

                                            <Pagination
                                                currentPage={currentPage}
                                                totalPages={totalPages}
                                                maxPagesToShow={rowsPerPage}
                                                onPageChange={(page) => setCurrentPage(page)}
                                            />

                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                    </div>
                    <CampaignFormDrawer isOpen={isDrawerOpen}
                        mode={drawerMode}
                        campaign={selectedCampaign}
                        onClose={() => setIsDrawerOpen(false)}
                        onSave={handleCampaignSaved} />
                </>
            }
        </div>

    )
}

export default CampaignList